import { Container, Row, Col, Tab, Nav } from "react-bootstrap";
import { ProjectCard } from "./ProjectCard";
import ReactGA from "react-ga4";
import { useState, useEffect, useRef } from "react";
import { fetchUserRepos, transformRepoToProject } from "../utils/githubService";
import { fetchLiveProjects, transformApiProject } from "../utils/portfolioService";

ReactGA.initialize("G-NY8FSE5QD1");

export const Projects = () => {
  const [githubRepos, setGithubRepos] = useState([]);
  const [liveProjects, setLiveProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setLiveLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const sectionRef = useRef(null);
  const INITIAL_PROJECTS_COUNT = 9;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsRevealed(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const loadGitHubRepos = async () => {
      try {
        setLoading(true);
        setError(null);
        const repos = await fetchUserRepos('Ushan2001', 100);
        const filteredRepos = repos
          .filter(repo => !repo.fork)
          .sort((a, b) => {
            if (b.stargazers_count !== a.stargazers_count) {
              return b.stargazers_count - a.stargazers_count;
            }
            return new Date(b.updated_at) - new Date(a.updated_at);
          });
        const transformedRepos = filteredRepos.map(transformRepoToProject);
        setGithubRepos(transformedRepos);
        setLastFetch(new Date());
      } catch (err) {
        console.error('Failed to load GitHub repos:', err);
        setError('Failed to load GitHub repositories. Showing cached projects.');
      } finally {
        setLoading(false);
      }
    };
    loadGitHubRepos();
  }, []);

  // ─── Fetch live projects from portfolio API ───────────────────────
  useEffect(() => {
    const loadLiveProjects = async () => {
      try {
        setLiveLoading(true);
        const apiProjects = await fetchLiveProjects();
        // Only keep projects that are NOT github links (i.e. true live/deployed sites)
        const transformed = apiProjects
          .filter(p => p.status === 'Active' && !p.projectLink?.includes('github.com'))
          .map(transformApiProject);
        setLiveProjects(transformed);
      } catch (err) {
        console.error('Failed to load live projects from portfolio API:', err);
        // Silently fall back — the static section will still show nothing
      } finally {
        setLiveLoading(false);
      }
    };
    loadLiveProjects();
  }, []);

  // Live projects come from the portfolio API (liveProjects state)
  // GitHub repos are fetched separately — both are merged for display
  const allProjects = [...liveProjects, ...githubRepos];

  const handleProjectClick = (projectTitle) => {
    ReactGA.event({
      category: "Project Click",
      action: `Clicked ${projectTitle}`,
      label: `${projectTitle} Link`,
    });
  };

  const cardStyle = {
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: '20px',
    padding: '28px',
    transition: 'all 0.3s ease',
  };

  return (
    <section className="project" id="projects" ref={sectionRef}>
      <Container>
        <Row>
          <Col size={12}>
            <div className={`reveal ${isRevealed ? 'revealed' : ''}`}>
              <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                <div className="section-tag" style={{ justifyContent: 'center' }}>
                  <span className="tag-dot"></span>
                  Portfolio
                </div>
                <h2 className="section-title">
                  Projects & <span className="gradient-text">More</span>
                </h2>
                <p className="section-subtitle" style={{ margin: '0 auto' }}>
                  A showcase of my work — from professional products to personal experiments and open-source contributions.
                </p>
              </div>

              <Tab.Container id="projects-tabs" defaultActiveKey="first">
                <Nav variant="pills" className="nav-pills mb-5 justify-content-center align-items-center">
                  <Nav.Item>
                    <Nav.Link eventKey="first" style={{ cursor: "pointer" }}>Projects</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="second" style={{ cursor: "pointer" }}>About</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="third" style={{ cursor: "pointer" }}>Certificates</Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  <Tab.Pane eventKey="first">
                    <Row style={{ rowGap: '24px' }}>
                      {loading && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#a0a0a8' }}>
                          <div style={{
                            width: '40px', height: '40px', margin: '0 auto 16px',
                            border: '3px solid rgba(6, 182, 212, 0.2)',
                            borderTopColor: '#06b6d4',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                          }} />
                          <p style={{ fontSize: '14px' }}>Loading repositories from GitHub...</p>
                        </div>
                      )}
                      {error && (
                        <div style={{
                          textAlign: 'center', padding: '14px',
                          background: 'rgba(236, 72, 153, 0.08)',
                          borderRadius: '12px', marginBottom: '16px',
                          border: '1px solid rgba(236, 72, 153, 0.2)',
                        }}>
                          <p style={{ color: '#ec4899', margin: 0, fontSize: '13px' }}>{error}</p>
                        </div>
                      )}
                      {lastFetch && !loading && githubRepos.length > 0 && (
                        <div style={{
                          textAlign: 'center', color: '#6b6b75', fontSize: '12px',
                          marginBottom: '16px', padding: '10px 16px',
                          background: 'rgba(255, 255, 255, 0.02)',
                          borderRadius: '8px', border: '1px solid rgba(255,255,255,0.04)',
                        }}>
                          <span style={{ color: '#06b6d4' }}>●</span> Live from GitHub · {lastFetch.toLocaleTimeString()} · {showAllProjects ? allProjects.length : Math.min(INITIAL_PROJECTS_COUNT, allProjects.length)} of {allProjects.length} projects
                        </div>
                      )}
                      {(showAllProjects ? allProjects : allProjects.slice(0, INITIAL_PROJECTS_COUNT)).map((project) => (
                        <ProjectCard
                          key={project.githubUrl || project.homepage || project.liveUrl || project.title}
                          {...project}
                          onClick={() => handleProjectClick(project.title)}
                        />
                      ))}
                    </Row>

                    {!showAllProjects && allProjects.length > INITIAL_PROJECTS_COUNT && (
                      <div style={{ textAlign: 'center', marginTop: '48px' }}>
                        <button
                          onClick={() => setShowAllProjects(true)}
                          className="btn-primary-glow"
                          style={{ padding: '14px 36px', border: 'none', cursor: 'pointer' }}
                        >
                          <span>Show All Projects</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        <p style={{ color: '#6b6b75', fontSize: '13px', marginTop: '12px' }}>
                          {allProjects.length - INITIAL_PROJECTS_COUNT} more available
                        </p>
                      </div>
                    )}

                    {showAllProjects && allProjects.length > INITIAL_PROJECTS_COUNT && (
                      <div style={{ textAlign: 'center', marginTop: '48px' }}>
                        <button
                          onClick={() => {
                            setShowAllProjects(false);
                            document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="btn-outline-glass"
                          style={{ padding: '14px 36px', cursor: 'pointer' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15" /></svg>
                          <span>Show Less</span>
                        </button>
                      </div>
                    )}
                  </Tab.Pane>

                  {/* ===== ABOUT TAB ===== */}
                  <Tab.Pane eventKey="second">
                    <div style={{ padding: '20px 0', maxWidth: '900px', margin: '0 auto' }}>
                      {/* Summary */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                        borderRadius: '20px', padding: '32px',
                        marginBottom: '24px',
                        border: '1px solid rgba(6, 182, 212, 0.15)',
                      }}>
                        <h3 style={{ color: '#f0f0f0', marginBottom: '16px', fontSize: '26px', fontWeight: '700' }}>
                          👋 Hello! I'm Ushan Mihiranga
                        </h3>
                        <p style={{ color: '#a0a0a8', fontSize: '15px', lineHeight: '1.8', marginBottom: 0 }}>
                          A passionate <b style={{ color: '#06b6d4' }}>third-year Software Engineering student</b> at SLIIT and{' '}
                          <b style={{ color: '#8b5cf6' }}>Full Stack Developer</b> at FonixEdu Pvt Ltd by Atlas Axillia. I specialize in creating seamless user experiences through modern front-end design and robust back-end functionality.
                        </p>
                      </div>

                      {/* Experience */}
                      <div style={{ ...cardStyle, marginBottom: '24px' }}>
                        <h4 style={{ color: '#f0f0f0', marginBottom: '16px', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '24px' }}>💼</span> Work Experience
                        </h4>
                        <div style={{ color: '#a0a0a8', fontSize: '14px', lineHeight: '1.8' }}>
                          <p style={{ marginBottom: '8px' }}>
                            <b style={{ color: '#f0f0f0', fontSize: '16px' }}>Full Stack Web Developer</b><br />
                            <span style={{ color: '#06b6d4' }}>FonixEdu Pvt Ltd by Atlas Axillia</span> · Feb 2024 - Present
                          </p>
                          <ul style={{ paddingLeft: '18px', marginTop: '10px' }}>
                            <li style={{ marginBottom: '6px' }}>Developing responsive web applications and landing pages</li>
                            <li style={{ marginBottom: '6px' }}>Building admin dashboards and client-side interfaces</li>
                            <li style={{ marginBottom: '6px' }}>Creating RESTful APIs and backend services</li>
                            <li style={{ marginBottom: '6px' }}>Integrating payment gateways and third-party services</li>
                            <li>Implementing real-time features using WebSocket</li>
                          </ul>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                        {/* Education */}
                        <div style={cardStyle}>
                          <h4 style={{ color: '#f0f0f0', marginBottom: '14px', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '24px' }}>🎓</span> Education
                          </h4>
                          <div style={{ color: '#a0a0a8', fontSize: '14px', lineHeight: '1.8' }}>
                            <p style={{ marginBottom: '14px' }}>
                              <b style={{ color: '#f0f0f0' }}>BSc (Hons) in Information Technology</b><br />
                              Specializing in Software Engineering<br />
                              SLIIT · 2022 - Present
                            </p>
                            <p>
                              <b style={{ color: '#f0f0f0' }}>G.C.E Advanced Level</b><br />
                              Physical Science — <b style={{ color: '#8b5cf6' }}>C3</b><br />
                              St. Aloysius College, Rathnapura · 2020
                            </p>
                          </div>
                        </div>

                        {/* Skills */}
                        <div style={cardStyle}>
                          <h4 style={{ color: '#f0f0f0', marginBottom: '14px', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '24px' }}>🚀</span> Skills Highlights
                          </h4>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                            {['React.js', 'Node.js', 'MongoDB', 'Express.js', 'React Native', 'AWS', 'Azure', 'Docker'].map((skill, idx) => (
                              <span key={idx} style={{
                                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(139, 92, 246, 0.15))',
                                color: '#f0f0f0', padding: '6px 14px', borderRadius: '20px',
                                fontSize: '13px', fontWeight: '500',
                                border: '1px solid rgba(6, 182, 212, 0.15)',
                              }}>
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Certs summary */}
                      <div style={{
                        ...cardStyle, textAlign: 'center', marginBottom: '24px',
                      }}>
                        <h4 style={{ color: '#f0f0f0', marginBottom: '12px', fontSize: '20px' }}>🏆 Certifications</h4>
                        <p style={{ color: '#a0a0a8', fontSize: '14px', marginBottom: '16px' }}>
                          Certified in AWS, Microsoft Azure, and Google Cloud & AI
                        </p>
                        <button
                          onClick={() => {
                            const tab = document.querySelector('[data-rr-ui-event-key="third"]');
                            if (tab) tab.click();
                          }}
                          className="btn-primary-glow"
                          style={{ padding: '12px 28px', border: 'none', cursor: 'pointer', fontSize: '14px' }}
                        >
                          <span>View All Certificates →</span>
                        </button>
                      </div>

                      {/* Contact */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                        borderRadius: '20px', padding: '32px',
                        border: '1px solid rgba(6, 182, 212, 0.15)',
                      }}>
                        <h4 style={{ color: '#f0f0f0', marginBottom: '20px', fontSize: '20px', textAlign: 'center' }}>
                          📞 Let's Connect!
                        </h4>
                        <div style={{
                          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                          gap: '14px', color: '#a0a0a8', fontSize: '14px',
                        }}>
                          <div><b style={{ color: '#f0f0f0' }}>📱 Phone:</b><br /><a href="tel:+94760673035" style={{ color: '#06b6d4', textDecoration: 'none' }}>+94 760 673 035</a></div>
                          <div><b style={{ color: '#f0f0f0' }}>📧 Email:</b><br /><a href="mailto:ushanmihiranga2017@gmail.com" style={{ color: '#06b6d4', textDecoration: 'none' }}>ushanmihiranga2017@gmail.com</a></div>
                          <div><b style={{ color: '#f0f0f0' }}>📍 Address:</b><br />168/6 First Lane, Welivita Road, Malabe</div>
                          <div><b style={{ color: '#f0f0f0' }}>💼 LinkedIn:</b><br /><a href="https://www.linkedin.com/in/ushan-mihiranga-b25846296" target="_blank" rel="noopener noreferrer" style={{ color: '#06b6d4', textDecoration: 'none' }}>View Profile</a></div>
                          <div><b style={{ color: '#f0f0f0' }}>💻 GitHub:</b><br /><a href="https://github.com/Ushan2001" target="_blank" rel="noopener noreferrer" style={{ color: '#06b6d4', textDecoration: 'none' }}>@Ushan2001</a></div>
                        </div>
                      </div>
                    </div>
                  </Tab.Pane>

                  {/* ===== CERTIFICATES TAB ===== */}
                  <Tab.Pane eventKey="third">
                    <div style={{ padding: '20px 0', maxWidth: '900px', margin: '0 auto' }}>
                      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <h3 style={{ color: '#f0f0f0', fontSize: '28px', fontWeight: '700', marginBottom: '12px' }}>
                          🏆 Professional Certifications
                        </h3>
                        <p style={{ color: '#a0a0a8', fontSize: '15px', maxWidth: '500px', margin: '0 auto' }}>
                          Continuously upskilling in cloud technologies and AI
                        </p>
                      </div>

                      {/* AWS */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(255, 153, 0, 0.08), rgba(255, 102, 0, 0.08))',
                        borderRadius: '20px', padding: '28px', marginBottom: '24px',
                        border: '1px solid rgba(255, 153, 0, 0.15)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" alt="AWS" style={{ width: '44px', height: '44px', filter: 'brightness(0) invert(1)' }} />
                          <h4 style={{ color: '#f0f0f0', fontSize: '22px', fontWeight: '700', margin: 0 }}>AWS SimuLearn Series</h4>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                          {['Core Security Concepts', 'Databases in Practice', 'Auto-Healing & Scaling Applications', 'Highly Available Web Applications', 'File Systems in the Cloud', 'Amazon S3 – Introduction'].map((cert, idx) => (
                            <div key={idx} style={{
                              background: 'rgba(255, 255, 255, 0.04)', padding: '12px 16px',
                              borderRadius: '12px', color: '#a0a0a8', fontSize: '14px', fontWeight: '500',
                              display: 'flex', alignItems: 'center', gap: '10px',
                              border: '1px solid rgba(255, 255, 255, 0.04)',
                            }}>
                              <span style={{ color: '#06b6d4', fontSize: '16px' }}>✓</span>
                              {cert}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Azure */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(0, 120, 212, 0.08), rgba(0, 90, 158, 0.08))',
                        borderRadius: '20px', padding: '28px', marginBottom: '24px',
                        border: '1px solid rgba(0, 120, 212, 0.15)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg" alt="Azure" style={{ width: '44px', height: '44px' }} />
                          <h4 style={{ color: '#f0f0f0', fontSize: '22px', fontWeight: '700', margin: 0 }}>Microsoft Azure Fundamentals</h4>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                          {['Describe Azure Identity, Access & Security', 'Explore Relational Database Services in Azure', 'Improve Scalability & Resiliency with Azure Load Balancer', 'Configure Azure Blob Storage', 'Add & Size Disks in Azure Virtual Machines'].map((cert, idx) => (
                            <div key={idx} style={{
                              background: 'rgba(255, 255, 255, 0.04)', padding: '12px 16px',
                              borderRadius: '12px', color: '#a0a0a8', fontSize: '14px', fontWeight: '500',
                              display: 'flex', alignItems: 'center', gap: '10px',
                              border: '1px solid rgba(255, 255, 255, 0.04)',
                            }}>
                              <span style={{ color: '#8b5cf6', fontSize: '16px' }}>✓</span>
                              {cert}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Google Cloud */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(66, 133, 244, 0.08), rgba(52, 168, 83, 0.08))',
                        borderRadius: '20px', padding: '28px', marginBottom: '24px',
                        border: '1px solid rgba(66, 133, 244, 0.15)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg" alt="Google Cloud" style={{ width: '44px', height: '44px' }} />
                          <h4 style={{ color: '#f0f0f0', fontSize: '22px', fontWeight: '700', margin: 0 }}>Google Cloud & AI Certifications</h4>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                          {['Introduction to Generative AI', 'Introduction to Large Language Models', 'Introduction to Responsible AI', 'Responsible AI: Applying AI Principles with Google Cloud', 'Prompt Design in Vertex AI Skills'].map((cert, idx) => (
                            <div key={idx} style={{
                              background: 'rgba(255, 255, 255, 0.04)', padding: '12px 16px',
                              borderRadius: '12px', color: '#a0a0a8', fontSize: '14px', fontWeight: '500',
                              display: 'flex', alignItems: 'center', gap: '10px',
                              border: '1px solid rgba(255, 255, 255, 0.04)',
                            }}>
                              <span style={{ color: '#10b981', fontSize: '16px' }}>✓</span>
                              {cert}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Stats */}
                      <div style={{
                        ...cardStyle, padding: '32px', textAlign: 'center',
                      }}>
                        <div style={{
                          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                          gap: '24px', maxWidth: '500px', margin: '0 auto',
                        }}>
                          {[
                            { num: '20+', label: 'Total Certifications' },
                            { num: '3', label: 'Cloud Platforms' },
                            { num: '5', label: 'AI Certifications' },
                          ].map((stat, idx) => (
                            <div key={idx}>
                              <div style={{
                                fontSize: '32px', fontWeight: '800',
                                background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text', marginBottom: '4px',
                              }}>
                                {stat.num}
                              </div>
                              <div style={{ color: '#6b6b75', fontSize: '13px', fontWeight: '500' }}>
                                {stat.label}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};
